import { useCallback, useEffect, useRef, useState } from 'react';

import { parseNdjsonStream } from '../lib/ndjson-parser';
import { useApiClient } from '../providers/auth-provider';

import type { SandboxLogRecord } from '../lib/api-client';

/**
 * Client-side display cap — the DVR window the console holds in memory.
 * Trimmed from the FRONT on live appends only; scroll-up pages the user
 * explicitly loaded are never trimmed out from under them mid-read.
 */
const MAX_LINES = 20_000;
/** Batch re-renders: streamed lines flush to state on this cadence. */
const FLUSH_INTERVAL_MS = 120;
/** First request replays this many newest records (then follows when live). */
export const INITIAL_TAIL = 200;
/** Page size for scroll-up history loads (`before=` reverse pagination). */
export const OLDER_PAGE = 150;

// eslint-disable-next-line no-control-regex
const ANSI_PATTERN = /\u001b\[[0-9;]*[A-Za-z]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_PATTERN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

/** Log lines are data, never terminal input — strip ANSI + control chars. */
export function sanitizeLogLine(line: string): string {
  return line.replace(ANSI_PATTERN, '').replace(CONTROL_PATTERN, '');
}

export interface SandboxLogEntry extends SandboxLogRecord {
  /** Client-assigned render key (js_seq is unique per stream read). */
  key: string;
}

export interface SandboxLogStreamFilters {
  follow: boolean;
  grep?: string | undefined;
  stream?: 'stdout' | 'stderr' | 'logger' | 'tap' | undefined;
  level?: 'debug' | 'info' | 'warning' | 'error' | undefined;
}

/**
 * Streaming ViewModel for one session's log console: opens the cell-log/1
 * NDJSON stream (newest `INITIAL_TAIL` records + optional live follow in one
 * request), batches arriving records into state, and pages OLDER history on
 * demand via `before=<min js_seq>` (the scroll-up path). Restarts cleanly
 * whenever the target session or filters change. Heartbeat pings are
 * consumed, never rendered.
 */
export function useSandboxRuntimeLogs(
  podSessionId: string | null,
  filters: SandboxLogStreamFilters,
  /** The session's tenant — acts-as a descendant for child-tenant sessions. */
  tenantId?: string,
) {
  const apiClient = useApiClient();
  const [entries, setEntries] = useState<SandboxLogEntry[]>([]);
  const [status, setStatus] = useState<'idle' | 'streaming' | 'ended' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  // null = unknown yet; true/false once the head of history is established.
  const [hasOlder, setHasOlder] = useState<boolean | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  const counterRef = useRef(0);
  const minSeqRef = useRef<number | null>(null);
  const loadingOlderRef = useRef(false);

  const toEntry = useCallback((record: SandboxLogRecord): SandboxLogEntry => {
    if (typeof record.line === 'string') record.line = sanitizeLogLine(record.line);
    if (typeof record.js_seq === 'number') {
      if (minSeqRef.current === null || record.js_seq < minSeqRef.current) {
        minSeqRef.current = record.js_seq;
      }
    }
    return { ...record, key: `${record.js_seq ?? 'x'}-${counterRef.current++}` };
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => {
    stop();
    setEntries([]);
    setError(undefined);
    setHasOlder(null);
    setLoadingOlder(false);
    minSeqRef.current = null;
    loadingOlderRef.current = false;
    if (!podSessionId) {
      setStatus('idle');
      return;
    }

    const runId = ++runIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('streaming');

    const pending: SandboxLogEntry[] = [];
    let replayCount = 0;
    const flush = () => {
      if (pending.length === 0) return;
      const batch = pending.splice(0, pending.length);
      setEntries((prev) => {
        const merged = [...prev, ...batch];
        return merged.length > MAX_LINES ? merged.slice(merged.length - MAX_LINES) : merged;
      });
    };
    const flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

    (async () => {
      try {
        const response = await apiClient.streamSandboxRuntimeLogs(
          podSessionId,
          {
            tail: INITIAL_TAIL,
            ...(filters.follow ? { follow: true } : {}),
            ...(filters.grep ? { grep: filters.grep } : {}),
            ...(filters.stream ? { stream: filters.stream } : {}),
            ...(filters.level ? { level: filters.level } : {}),
          },
          { signal: controller.signal, ...(tenantId ? { tenantId } : {}) },
        );
        if (!response.ok) {
          let detail = `Log stream failed (${response.status})`;
          try {
            const body = (await response.json()) as { detail?: string };
            if (body.detail) detail = body.detail;
          } catch {
            /* non-JSON problem body */
          }
          throw new Error(detail);
        }
        if (!response.body) throw new Error('Log stream had no body');
        for await (const record of parseNdjsonStream<SandboxLogRecord>(response.body)) {
          if (runIdRef.current !== runId) return;
          if (record.event === 'ping') continue;
          replayCount++;
          if (replayCount === INITIAL_TAIL) {
            // A full initial tail means history likely extends further back.
            setHasOlder(true);
          }
          pending.push(toEntry(record));
        }
        if (runIdRef.current === runId) {
          flush();
          setStatus('ended');
          if (replayCount < INITIAL_TAIL) setHasOlder(false);
        }
      } catch (err: unknown) {
        if (runIdRef.current !== runId || controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Log stream failed');
        setStatus('error');
      }
    })();

    return () => {
      clearInterval(flushTimer);
      flush();
      controller.abort();
    };
    // filters is object-identity-unstable by design; callers memoize it.
  }, [podSessionId, filters, apiClient, stop, toEntry, tenantId]);

  /**
   * Load the page of history immediately preceding the oldest loaded record.
   * Resolves with the number of prepended records so the caller can anchor
   * the scroll position around the prepend.
   */
  const loadOlder = useCallback(async (): Promise<number> => {
    const before = minSeqRef.current;
    if (!podSessionId || before === null || before <= 1 || loadingOlderRef.current) return 0;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const response = await apiClient.streamSandboxRuntimeLogs(
        podSessionId,
        {
          before,
          tail: OLDER_PAGE,
          ...(filters.grep ? { grep: filters.grep } : {}),
          ...(filters.stream ? { stream: filters.stream } : {}),
          ...(filters.level ? { level: filters.level } : {}),
        },
        tenantId ? { tenantId } : undefined,
      );
      if (!response.ok || !response.body) {
        throw new Error(`History page failed (${response.status})`);
      }
      const page: SandboxLogEntry[] = [];
      for await (const record of parseNdjsonStream<SandboxLogRecord>(response.body)) {
        if (record.event === 'ping') continue;
        page.push(toEntry(record));
      }
      if (page.length > 0) setEntries((prev) => [...page, ...prev]);
      setHasOlder(page.length >= OLDER_PAGE);
      return page.length;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'History page failed');
      return 0;
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [podSessionId, filters, apiClient, toEntry, tenantId]);

  const clear = useCallback(() => setEntries([]), []);

  return { entries, status, error, hasOlder, loadingOlder, loadOlder, clear, stop };
}
